import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { initials } from "../../lib/assignees";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { InviteResultDialog } from "./InviteResultDialog";
import { Plus, Trash2, Send, Users, ShieldCheck, Clock } from "lucide-react";

const inputCls = "mt-1.5 w-full h-10 px-3.5 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white dark:focus:bg-white/15 transition-all";
const labelCls = "text-xs font-medium text-slate-500 uppercase tracking-wider";
const EMPTY = { name: "", email: "", role: "customer", customer_id: "", password: "" };

export const AdminUsers = ({ customers, teams, currentUserId, onChanged }) => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const pwWeak = form.password && !(form.password.length >= 8 && /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(form.password) && /\d/.test(form.password));
  const submit = async (e) => {
    e.preventDefault();
    if (pwWeak) { toast.error("Şifre en az 8 karakter olmalı ve harf ile rakam içermeli"); return; }
    setSaving(true);
    try {
      const payload = { ...form, customer_id: form.role === "customer" ? form.customer_id : null, password: form.password || null };
      const { data } = await api.post("/admin/users", payload);
      setOpen(false);
      setForm(EMPTY);
      load();
      onChanged?.();
      if (Object.prototype.hasOwnProperty.call(data, "email_sent")) setResult({ ...data, email: data.user.email });
      else toast.success("Kullanıcı oluşturuldu");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resend = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/invite`);
      setResult({ ...data, email: u.email });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.name} silinsin mi? Atamaları ve grup üyelikleri kaldırılır.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const teamNames = (uid) => teams.filter((t) => (t.member_ids || []).includes(uid)).map((t) => t.name);

  return (
    <div className="animate-fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Kullanıcılar</h1>
          <p className="text-sm text-slate-500 mt-1.5">Plena yöneticileri ve müşteri kişileri · davetle hesap açın</p>
        </div>
        <button data-testid="admin-add-user-button" onClick={() => { setForm(EMPTY); setOpen(true); }}
          className="h-10 px-4 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] active:scale-[0.98] text-white text-sm font-semibold transition-all shadow-lg shadow-[#0066CC]/25 flex items-center gap-2">
          <Plus size={16} /> Kullanıcı Ekle
        </button>
      </div>

      <div data-testid="admin-user-list" className="bg-white/90 dark:bg-[#1C1C1E]/90 backdrop-blur-md rounded-2xl border border-slate-200/70 dark:border-white/10 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden">
        {users.map((u, i) => (
          <div key={u.id} data-testid="admin-user-row" className={`flex items-center gap-4 px-5 py-3.5 ${i ? "border-t border-slate-100 dark:border-white/5" : ""}`}>
            <span className={`w-9 h-9 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0 ${u.role === "admin" ? "bg-slate-900 dark:bg-slate-600" : "bg-[#007AFF]"}`}>{initials(u.name)}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{u.name}</p>
                {u.role === "admin" ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-white/10 rounded-full px-2 py-0.5 flex items-center gap-1"><ShieldCheck size={10} /> Plena</span>
                ) : (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[#0066CC] bg-[#007AFF]/10 rounded-full px-2 py-0.5 truncate max-w-[160px]">{u.customer_name || "Müşteri"}</span>
                )}
                {u.status === "invited" && (
                  <span data-testid="admin-user-invited-badge" className="text-[10px] font-semibold uppercase tracking-wider text-[#FF9500] bg-[#FF9500]/10 rounded-full px-2 py-0.5 flex items-center gap-1"><Clock size={10} /> Davet bekliyor</span>
                )}
              </div>
              <p className="text-xs text-slate-500 truncate">{u.email}{teamNames(u.id).length > 0 && <span className="text-slate-400"> · <Users size={10} className="inline -mt-0.5" /> {teamNames(u.id).join(", ")}</span>}</p>
            </div>
            <button data-testid="admin-user-invite-button" onClick={() => resend(u)} title={u.status === "invited" ? "Daveti yeniden gönder" : "Şifre sıfırlama gönder"}
              className="h-8 px-3 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-1.5 transition-colors">
              <Send size={12} /> {u.status === "invited" ? "Daveti Gönder" : "Şifre Sıfırla"}
            </button>
            {u.id !== currentUserId && (
              <button data-testid="admin-user-delete-button" onClick={() => remove(u)}
                className="w-8 h-8 rounded-lg hover:bg-[#FF2D55]/10 text-slate-400 hover:text-[#FF2D55] flex items-center justify-center transition-colors">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
        {users.length === 0 && <div className="text-center py-14 text-slate-400 text-sm">Henüz kullanıcı yok.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Kullanıcı Ekle</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-slate-100 dark:bg-white/10">
              {[["customer", "Müşteri kişisi"], ["admin", "Plena yöneticisi"]].map(([v, l]) => (
                <button type="button" key={v} data-testid={`user-role-${v}`} onClick={() => setForm({ ...form, role: v })}
                  className={`h-9 rounded-lg text-sm font-medium transition-all ${form.role === v ? "bg-white dark:bg-[#3A3A3C] shadow-sm text-slate-900 dark:text-white" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
            <div>
              <label className={labelCls}>Ad Soyad</label>
              <input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ayşe Yılmaz" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>E-posta</label>
              <input data-testid="user-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ayse@firma.com" className={inputCls} />
            </div>
            {form.role === "customer" && (
              <div>
                <label className={labelCls}>Müşteri</label>
                <select data-testid="user-customer-select" required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className={inputCls}>
                  <option value="">Müşteri seçin</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={labelCls}>Şifre <span className="normal-case tracking-normal text-slate-400">(boş bırakılırsa davet e-postası gönderilir)</span></label>
              <input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Opsiyonel · en az 8 karakter, harf + rakam" className={inputCls} />
              {pwWeak && <p data-testid="user-password-hint" className="text-[11px] text-[#FF2D55] mt-1">En az 8 karakter, en az bir harf ve bir rakam gerekli</p>}
            </div>
            <button data-testid="user-create-submit-button" type="submit" disabled={saving}
              className="w-full h-10 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] text-white text-sm font-semibold transition-all disabled:opacity-60 flex items-center justify-center gap-2">
              {form.password ? <Plus size={14} /> : <Send size={14} />} {saving ? "Kaydediliyor..." : form.password ? "Oluştur" : "Davet Gönder"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <InviteResultDialog result={result} onClose={() => setResult(null)} />
    </div>
  );
};

export default AdminUsers;
